<template>
  <div class="voice-input">
    <div class="voice-actions">
      <v-btn
        v-if="!isRecording"
        color="primary"
        variant="outlined"
        prepend-icon="mdi-microphone"
        @click="startRecording"
      >
        Record voice
      </v-btn>
      <v-btn
        v-else
        color="error"
        variant="flat"
        prepend-icon="mdi-stop-circle-outline"
        @click="stopRecording"
      >
        Stop {{ formattedDuration }}
      </v-btn>

      <v-btn
        variant="outlined"
        prepend-icon="mdi-file-music-outline"
        :disabled="isRecording"
        @click="fileInput?.click()"
      >
        Choose audio file
      </v-btn>

      <input
        ref="fileInput"
        type="file"
        multiple
        class="d-none"
        accept=".flac,.mp3,.mp4,.mpeg,.mpga,.m4a,.ogg,.wav,.webm"
        @change="handleFileSelection"
      />
    </div>

    <p class="text-caption text-medium-emphasis mt-2 mb-0">
      Record a voice memory or choose a FLAC, MP3, MP4, MPEG, MPGA, M4A, OGG, WAV, or WebM file up to 25 MB. A transcript will be added after upload.
    </p>

    <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mt-3">
      {{ errorMessage }}
    </v-alert>

    <div v-if="modelValue.length" class="voice-preview-list mt-4">
      <div v-for="(audio, index) in modelValue" :key="audio.id" class="voice-preview">
        <div class="voice-preview-icon">
          <v-icon size="32">mdi-waveform</v-icon>
        </div>
        <div class="voice-preview-content">
          <div class="voice-preview-name">{{ audio.name }}</div>
          <audio :src="audio.url" controls preload="metadata"></audio>
          <v-textarea
            :model-value="audio.caption"
            label="Add a note about this voice memory"
            variant="outlined"
            rows="2"
            auto-grow
            hide-details
            class="mt-3"
            @update:model-value="updateCaption(index, $event)"
          />
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          color="error"
          size="small"
          aria-label="Remove audio"
          @click="removeAudio(index)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'

const MAX_AUDIO_BYTES = 25 * 1024 * 1024
const SUPPORTED_EXTENSIONS = new Set(['flac', 'mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'ogg', 'wav', 'webm'])
const SUPPORTED_FORMATS_MESSAGE = 'Supported formats: FLAC, MP3, MP4, MPEG, MPGA, M4A, OGG, WAV, and WebM.'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue'])

const fileInput = ref(null)
const isRecording = ref(false)
const recordingSeconds = ref(0)
const errorMessage = ref('')
let mediaRecorder = null
let mediaStream = null
let timer = null
let chunks = []

const formattedDuration = computed(() => {
  const minutes = Math.floor(recordingSeconds.value / 60).toString().padStart(2, '0')
  const seconds = (recordingSeconds.value % 60).toString().padStart(2, '0')
  return `${minutes}:${seconds}`
})

const preferredMimeType = () => {
  const types = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm']
  return types.find(type => window.MediaRecorder?.isTypeSupported(type)) || ''
}

const startRecording = async () => {
  errorMessage.value = ''
  if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
    errorMessage.value = 'Voice recording is not supported in this browser. You can still choose an audio file.'
    return
  }

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const mimeType = preferredMimeType()
    mediaRecorder = mimeType ? new MediaRecorder(mediaStream, { mimeType }) : new MediaRecorder(mediaStream)
    chunks = []
    mediaRecorder.ondataavailable = event => {
      if (event.data?.size) chunks.push(event.data)
    }
    mediaRecorder.onstop = finishRecording
    mediaRecorder.start(1000)
    recordingSeconds.value = 0
    isRecording.value = true
    timer = window.setInterval(() => { recordingSeconds.value += 1 }, 1000)
  } catch (error) {
    errorMessage.value = error?.name === 'NotAllowedError'
      ? 'Microphone access was declined. Allow access or choose an audio file instead.'
      : 'The microphone could not be started. Please try choosing an audio file.'
    stopTracks()
  }
}

const stopRecording = () => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop()
  isRecording.value = false
  if (timer) window.clearInterval(timer)
  timer = null
  stopTracks()
}

const finishRecording = () => {
  const mimeType = mediaRecorder?.mimeType || 'audio/webm'
  const extension = mimeType.includes('mp4') ? 'm4a' : 'webm'
  const blob = new Blob(chunks, { type: mimeType })
  const file = new File([blob], `voice-memory-${Date.now()}.${extension}`, { type: mimeType })
  addFiles([file])
  chunks = []
}

const stopTracks = () => {
  mediaStream?.getTracks().forEach(track => track.stop())
  mediaStream = null
}

const handleFileSelection = event => {
  addFiles(Array.from(event.target.files || []))
  event.target.value = ''
}

const addFiles = files => {
  errorMessage.value = ''
  const additions = []
  for (const file of files) {
    const extension = (file.name?.split('.').pop() || '').toLowerCase()
    if (!SUPPORTED_EXTENSIONS.has(extension)) {
      errorMessage.value = `${file.name} is not a supported audio file. ${SUPPORTED_FORMATS_MESSAGE}`
      continue
    }
    if (file.size > MAX_AUDIO_BYTES) {
      errorMessage.value = `${file.name} is larger than 25 MB.`
      continue
    }
    additions.push({
      id: `${Date.now()}-${Math.random()}`,
      file,
      name: file.name,
      url: URL.createObjectURL(file),
      caption: ''
    })
  }
  if (additions.length) emit('update:modelValue', [...props.modelValue, ...additions])
}

const updateCaption = (index, caption) => {
  const updated = props.modelValue.map((audio, audioIndex) => audioIndex === index ? { ...audio, caption } : audio)
  emit('update:modelValue', updated)
}

const removeAudio = index => {
  const removed = props.modelValue[index]
  if (removed?.url?.startsWith('blob:')) URL.revokeObjectURL(removed.url)
  emit('update:modelValue', props.modelValue.filter((_, audioIndex) => audioIndex !== index))
}

onBeforeUnmount(() => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.onstop = null
    mediaRecorder.stop()
  }
  if (timer) window.clearInterval(timer)
  stopTracks()
})
</script>

<style scoped>
.voice-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.voice-preview-list {
  display: grid;
  gap: 16px;
}

.voice-preview {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 40px;
  gap: 16px;
  align-items: start;
  padding: 16px;
  border: 1px solid rgba(88, 65, 61, 0.16);
  border-radius: 14px;
  background: #fffaf8;
}

.voice-preview-icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  color: white;
  background: linear-gradient(145deg, #9b5149, #6f342f);
}

.voice-preview-name {
  overflow: hidden;
  margin-bottom: 8px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.voice-preview audio {
  width: 100%;
  max-width: 520px;
}

@media (max-width: 600px) {
  .voice-preview {
    grid-template-columns: 48px minmax(0, 1fr) 32px;
    gap: 10px;
    padding: 12px;
  }

  .voice-preview-icon {
    width: 48px;
    height: 48px;
  }
}
</style>
